import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { GenericPostRequest, IssueOverviewModel, IssueType, PaginationPage, PaginationResponse, ServerGeneratedIssueId, User } from 'src/app/classes';
import { serverLocation } from 'src/app/globals';
import { WebsocketService } from 'src/app/services/websocket.service';
import { ContractItem } from '../../contract/classes';
import { Customer } from '../../customer/classes';
import { DefaultNotification } from '../../default-notification/classes';
import { EMailAudit } from '../../mail/classes';
import { Offer } from '../../offer/classes';
import { IssueTypes } from '../../shared/globals';
import { TokenService } from '../../shared/services/token.service';
import { Issue, IssueAuditStatus, IssueCategory, IssueItem, IssueItemContractMap, IssuePriority, IssueStatus, IssueTransaction, TransformIssueItemTask } from '../classes';

@Injectable({
  providedIn: 'root'
})
export class IssueService {

  private _issues: { [key: number]: ReplaySubject<Issue> } = {};
  issueStatus: { [key: number]: ReplaySubject<IssueStatus> } = {};
  issueCategories: { [key: number]: ReplaySubject<IssueCategory> } = {};
  issuePriorities: { [key: number]: ReplaySubject<IssuePriority> } = {};

  public changes$ = combineLatest(Object.values(this._issues)).pipe(map(x => x.filter(y => y.issuetype === IssueTypes.Change)));
  public issues$ = combineLatest(Object.values(this._issues)).pipe(map(x => x.filter(y => y.issuetype === IssueTypes.Issue)));
  public incidents$ = combineLatest(Object.values(this._issues)).pipe(map(x => x.filter(y => y.issuetype === IssueTypes.Incident)));
  public projects$ = combineLatest(Object.values(this._issues)).pipe(map(x => x.filter(y => y.issuetype === IssueTypes.Project)));

  private _allIssueStatusLoaded = new BehaviorSubject(false);
  allIssueStatusLoaded$ = this._allIssueStatusLoaded.asObservable();

  private _allIssueCategoriesLoaded = new BehaviorSubject(false);
  allIssueCategoriesLoaded$ = this._allIssueCategoriesLoaded.asObservable();

  private _allIssuePrioritiesLoaded = new BehaviorSubject(false);
  allIssuePrioritiesLoaded$ = this._allIssuePrioritiesLoaded.asObservable();

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService,
    private _websocketService: WebsocketService
  ) {
    this._websocketService.issueChanged$.subscribe((value) => {
      if (this._issues[value.identifier]) this.getIssueById(value.identifier, false).toPromise();
    });
  }

  addIssueItem(itemControl: FormArray, formBuilder: FormBuilder) {
    var position = Math.max(0, ...(itemControl.value as IssueItem[]).map(x => x.itemposition)) + 1;
    itemControl.push(formBuilder.group({
      'itembuildindate': null,
      'itemcanceldate': null,
      'itemcontract': null,
      'itemcount': null,
      'itemdesc': null,
      'itemfaktdate': null,
      'itemid': null,
      'iteminfo': null,
      'itemissue': null,
      'itemlocation': null,
      'itemposition': position,
      'itemproduct': null,
      'itempurchasecost': null,
      'itemrentalcost': null,
      'itemsetupcost': null,
      'itemname': null
    }));
  }

  assignUser(issueId: number, userId: string): Observable<any> {
    return this._httpClient.get(`${serverLocation}/api/issue/${issueId}/assign/${userId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }
  /**
   * the method creates a contract item for a single issue item
   */
  bookIssueItem(data: TransformIssueItemTask): Observable<ContractItem> {
    return this._httpClient.post(`${serverLocation}/api/issue-item/transform-separately-to-contract`, data, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ContractItem>;
  }

  checkIssueVersion(issue: Issue): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/issue/${issue.issueid}/check-version/${issue.concurrencystamp}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  clearChangeHistory = () => this._httpClient.delete(`${serverLocation}/api/change/history`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  });

  clearIncidentHistory = () => this._httpClient.delete(`${serverLocation}/api/incident/history`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  });

  clearIssueHistory = () => this._httpClient.delete(`${serverLocation}/api/issue/history`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  });

  createIssue(issue: Issue, note: string = null): Observable<Issue> {
    return this._httpClient.post(`${serverLocation}/api/issue`, {
      content: issue,
      note: note
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Issue>;
  }

  createIssueItem(issueItem: IssueItem, note: string = null): Observable<IssueItem> {
    return this._httpClient.post(`${serverLocation}/api/issue-item`, {
      content: issueItem,
      note: note
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssueItem>;
  }

  deleteIssue(issueId: number): Observable<Object> {
    let subject = new Subject();
    this._deleteIssueById(issueId).pipe(tap(() => {
      if (this._issues[issueId]) {
        this._issues[issueId].complete();
        delete this._issues[issueId];
      }
    })).subscribe(subject);
    return subject;
  }

  deleteIssueItem(itemId: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/issue-item/${itemId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  getCustomerDefaultNotifications(): Observable<DefaultNotification[]> {
    return this._httpClient.get(`${serverLocation}/api/default-notification/customer-default`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<DefaultNotification[]>;
  }

  getCustomerIssues = (customerId: number): Observable<Issue[]> => (this._httpClient.get(`${serverLocation}/api/issue/by-customer/${customerId}`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  }) as Observable<Issue[]>);

  getAssignedUsers(issueId: number): Observable<string[]> {
    return this._httpClient.get(`${serverLocation}/api/issue/${issueId}/assigned-users`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<string[]>;
  }
  
  getIssueItems = (issueId: number): Observable<IssueItem[]> => (this._httpClient.get(`${serverLocation}/api/issue/${issueId}/issue-items`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  }) as Observable<IssueItem[]>);

  getIssueMails = (issueId: number): Observable<EMailAudit[]> => this._httpClient.get(`${serverLocation}/api/issue/${issueId}/emails`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  }) as Observable<EMailAudit[]>;

  /**
   * the method returns an issue's offers
   * @param issueId 
   * @returns offer collection
   */
  getIssueOffers(issueId: number): Observable<Offer[]> {
    return this._httpClient.get(`${serverLocation}/api/issue/${issueId}/offers`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Offer[]>;
  }

  incidentCopiedIntoIssue(incidentId: number, issueId: number){
    return this._httpClient.get(`${serverLocation}/api/issue/${incidentId}/transform-to-issue/${issueId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  issueIsWatched(issueId: number): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/issue/${issueId}/watched`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  getIssueAuditStatus(guid: string): Observable<IssueAuditStatus> {
    return this._httpClient.get(`${serverLocation}/api/issue/audit-status/${guid}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssueAuditStatus>;
  }

  getAllIssueCategories(soft: boolean = true): Observable<IssueCategory[]> {
    if (!soft || !this._allIssueCategoriesLoaded.value) {
      return this._getAllIssueCategories().pipe(
        tap(
          (categories: IssueCategory[]) => {
            categories.forEach(x => {
              if (!soft && this.issueCategories[x.categoryid]) this.issueCategories[x.categoryid].next(x);
              else {
                if (!this.issueCategories[x.categoryid]) this.issueCategories[x.categoryid] = new ReplaySubject(1);
                this.issueCategories[x.categoryid].next(x);
              }
            });
          }
        )
      );
    }
    return combineLatest(Object.values(this.issueCategories));
  }

  getAllIssuePriorities(soft: boolean = true): Observable<IssuePriority[]> {
    if (!soft || !this._allIssuePrioritiesLoaded.value) {
      return this._getAllIssuePriorities().pipe(
        tap(
          (priorities: IssuePriority[]) => {
            priorities.forEach(x => {
              if (!soft && this.issuePriorities[x.priorityid]) this.issuePriorities[x.priorityid].next(x);
              else {
                if (!this.issuePriorities[x.priorityid]) this.issuePriorities[x.priorityid] = new ReplaySubject(1);
                this.issuePriorities[x.priorityid].next(x);
              }
            });
          }
        )
      );
    }
    return combineLatest(Object.values(this.issuePriorities));
  }

  getAllIssueStatus(soft: boolean = true): Observable<IssueStatus[]> {
    if (!soft || !this._allIssueStatusLoaded.value) {
      return this._getAllIssueStatus().pipe(
        tap(
          (status: IssueStatus[]) => {
            status.forEach(x => {
              if (!soft && this.issueStatus[x.stateid]) this.issueStatus[x.stateid].next(x);
              else {
                if (!this.issueStatus[x.stateid]) this.issueStatus[x.stateid] = new ReplaySubject(1);
                this.issueStatus[x.stateid].next(x);
              }
            });
          }
        )
      );
    }
    return combineLatest(Object.values(this.issueStatus));
  }

  getAssignedIssues(uri: string, page: PaginationPage): Observable<PaginationResponse<IssueOverviewModel[]>> {
    return this._httpClient.post(`${serverLocation}/api/${uri}/assigned`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  getDefaultNotifications(page: PaginationPage): Observable<PaginationResponse<DefaultNotification[]>> {
    return this._httpClient.post(`${serverLocation}/api/default-notification/search`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<DefaultNotification[]>>;
  }

  getIssueById(identifier: number, soft: boolean = true): Observable<Issue> {
    if (typeof identifier !== 'number') return of(null);
    if (!soft || !this._issues[identifier]) {
      if (!this._issues[identifier]) this._issues[identifier] = new ReplaySubject(1);
      this._getIssueById(identifier).subscribe({
        next: (issue: Issue) => this._issues[identifier].next(issue),
        error: (error: HttpErrorResponse) => this._issues[identifier].error(error)
      });
    }
    return this._issues[identifier].asObservable();
  }

  getIssueChangeHistory(identifier: number, page: PaginationPage = { pageNumber: 1, pageSize: 5 }): Observable<PaginationResponse<any>> {
    return this._httpClient.post(`${serverLocation}/api/issue/change-audit/${identifier}`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<any>>;
  }

  getIssueStatusById(identifier: number, soft: boolean = true): Observable<IssueStatus> {
    if (typeof identifier !== 'number') return of(null);
    if (!soft || !this.issueStatus[identifier]) {
      if (!this.issueStatus[identifier]) this.issueStatus[identifier] = new ReplaySubject(1);
      this._getIssueStatusById(identifier).subscribe({
        next: (status: IssueStatus) => this.issueStatus[identifier].next(status),
        error: (error: HttpErrorResponse) => this.issueStatus[identifier].error(error)
      });
    }
    return this.issueStatus[identifier].asObservable();
  }

  getLatestIssues(uri: string, page: PaginationPage): Observable<PaginationResponse<IssueOverviewModel[]>> {
    return this._httpClient.post(`${serverLocation}/api/${uri}/latest`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  getUnseenIssues(uri: string, page: PaginationPage): Observable<PaginationResponse<IssueOverviewModel[]>> {
    return this._httpClient.post(`${serverLocation}/api/${uri}/unseen`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  getUserIssueHistory(uri: string, page: PaginationPage): Observable<PaginationResponse<IssueOverviewModel[]>> {
    return this._httpClient.post(`${serverLocation}/api/${uri}/history`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  getWatchedIssues(uri: string, page: PaginationPage): Observable<PaginationResponse<IssueOverviewModel[]>> {
    return this._httpClient.post(`${serverLocation}/api/${uri}/watched`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  loadNextIssueId(): Observable<ServerGeneratedIssueId> {
    return this._httpClient.get(`${serverLocation}/api/issue/generateId`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ServerGeneratedIssueId>;
  }

  loadIssueTransactions(state: number): Observable<IssueTransaction[]> {
    return this._httpClient.get(`api/issue-transaction/status/${state}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssueTransaction[]>
  }

  overwriteControlSlip(issueId: number): Observable<Object> {
    return this._httpClient.get(`${serverLocation}/api/issue/overwrite-control-slip/${issueId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  saveIssueHistory(issueId: number): Observable<Object> {
    return this._httpClient.get(`${serverLocation}/api/issue/history/${issueId}`, {
        headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
      }) as Observable<Object>;
  }

  searchChangesByUserFilter(identifier: number, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/change/user-filter-search/${identifier}`, paginationPage, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  searchChangesByString(search: string, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/change/search`, {
      'searchString': search,
      'page': paginationPage
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  searchIncidentsByUserFilter(identifier: number, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/incident/user-filter-search/${identifier}`, paginationPage, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  searchIncidentsByString(search: string, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/incident/search`, {
      'searchString': search,
      'page': paginationPage
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  searchIssuesByUserFilter(identifier: number, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/issue/user-filter-search/${identifier}`, paginationPage, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  searchIssuesByString(search: string, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/issue/search`, {
      'searchString': search,
      'page': paginationPage
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  searchProjectsByUserFilter(identifier: number, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/project/user-filter-search/${identifier}`, paginationPage, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  searchProjectsByString(search: string, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/project/search`, {
      'searchString': search,
      'page': paginationPage
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<IssueOverviewModel[]>>;
  }

  setIssueWatchedStatus(issueId: number, watch: boolean): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/issue/${issueId}/${watch? 'watch': 'unwatch'}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  transformIssueItems(map: IssueItemContractMap[]): Observable<ContractItem[]> {
    return this._httpClient.post(`${serverLocation}/api/issue-item/transform-to-contract-items`, {
      map: map
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ContractItem[]>;
  }

  unassignUser(issueId: number, userId: string): Observable<any> {
    return this._httpClient.get(`${serverLocation}/api/issue/${issueId}/unassign/${userId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    });
  }

  updateIssue(issue: Issue, note: string = null): Observable<Issue> {
    return (this._httpClient.put(`${serverLocation}/api/issue`, {
      content: issue,
      note: note
    } as GenericPostRequest<Issue>, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Issue>).pipe(tap((result) => this._issues[result.issueid] && this._issues[result.issueid].next(issue)));
  }

  updateIssueItem(issueItem: IssueItem, note: string = null): Observable<IssueItem> {
    return this._httpClient.put(`${serverLocation}/api/issue-item`, {
      content: issueItem,
      note: note
    } as GenericPostRequest<IssueItem>, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssueItem>;
  }

  private _deleteIssueById(identifier: number): Observable<Issue> {
    return this._httpClient.delete(`${serverLocation}/api/issue/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Issue>;
  }

  private _getAllIssueCategories(): Observable<IssueCategory[]> {
    return (this._httpClient.get(`${serverLocation}/api/issue-category`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssueCategory[]>).pipe(tap(() => this._allIssueCategoriesLoaded.next(true)));
  }

  private _getAllIssuePriorities(): Observable<IssuePriority[]> {
    return (this._httpClient.get(`${serverLocation}/api/priority`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssuePriority[]>).pipe(tap(() => this._allIssuePrioritiesLoaded.next(true)));
  }

  private _getAllIssueStatus(): Observable<IssueStatus[]> {
    return (this._httpClient.get(`${serverLocation}/api/issue-status`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssueStatus[]>).pipe(tap(() => this._allIssueStatusLoaded.next(true)));
  }

  private _getIssueById(identifier: number): Observable<Issue> {
    return this._httpClient.get(`${serverLocation}/api/issue/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Issue>;
  }

  private _getIssueStatusById(identifier: number): Observable<IssueStatus> {
    return this._httpClient.get(`${serverLocation}/api/issue-status/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<IssueStatus>;
  }
}

class ReplaceNotificationDataWrapper {
  issue?: Issue;
  issueCreator?: User;
  issueState?: IssueStatus;
  issueType?: IssueType;
  issueCustomer?: Customer;
  transaction?: IssueTransaction;
  transactionResultState?: IssueStatus;
}
